<template lang="pug">
#careimage-register(v-cloak)
  .box.m-0
    .box-header.with-border.register-header
      .title
        span 介護記録登録
    .box-body.register-body
      FormLabel(key-name="記録日" :value="date")
      FormLabel(key-name="利用者名" :value="residentName")
      .register-row
        .register-group
          .register-key
            .left-side.d-flex
              span.key-name 
              span.comment ※最大4枚まで登録可能です
            .right-side
              HelpIcon(:title="'選択可能なファイルについて'" :text="'jpegもしくはpdfを選択可能です。pdfはjpegファイルに変換してご家族に送ります。'")

          .register-value
            MultiFileSelecter(
              ref="fileSelecter"
              :mimetypes="['image/jpeg', 'application/pdf']"
              width="450px"
              height="74px"
              :limit="_registerImageFilesLimit"
              :showPreview="true"
              :enablePreviewStatus="_enablePreviewStatus"
              @input="inputFiles"
              @preview="onPreview"
            )

          input(name="careimage-multiple-register-errors" data-vv-as="介護記録" style="display: none")

          .error-messages.text-break(v-for="error in errors.collect('careimage-multiple-register-errors')" :style="{ 'maxWidth': '600px' }")
            label.error-message {{ error }}
            br
      .register-row
        DraggableFiles(v-model="imageFiles" @delete="onFileDelete" :item-width="'468px'")

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="onRegisterCareimage()" type="button") 登録
        button.btn.cancel-btn(@click="gotoCareimageList()" type="button") キャンセル
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoCareimageList")
  CareimageModal(:show-delete="false" ref="careimageModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import FileReaderWrapper from "@/commons/utils/FileReaderWrapper";
import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import CareimagesService from "@/commons/services/Careimages";
import CareimageModel from "@/commons/models/Careimage";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType, S3LocationCareimage } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import SystemError from "@/commons/models/SystemError";
import CareimageCommonService, { CareimageFileModel } from "@/commons/services/CareimageCommon";

import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";
import CareimageModal from "@/pc/components/modals/CareimageModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";

/**
 * 介護記録登録画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OkModal,
    CareimageModal,
    PageLoading,
    ValidationAlert,
    FormLabel,
    MultiFileSelecter,
    DraggableFiles
  },
  $_veeValidate: { validator: "new" }
})
export default class CareimageRegister extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 介護記録サービス */
  @Inject("CareimagesService") careimagesService!: CareimagesService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 記録日 */
  date: string = "";
  /** 利用者名 */
  residentName: string = "";
  /** 部屋名 */
  residentRoom: string = "";
  /** 介護記録画像登録数 */
  readonly registerFileUploadLimit: number = Setting.careimage.fileUploadLimit;

  /** 実際に登録する介護記録 */
  imageFiles: Array<CareimageFileModel> = [];

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const residentId: number = Number(this.$route.params.id);

    // 測定日は前画面から渡された値を使用する
    // Urlから取得した情報は一律string型になっているのでNumberに直す
    const measuringDate: string = this.$dateUtil.convertNumberToString(Number(this.$route.params.date), "yyyy年mm月dd日", false);
    this.date = measuringDate;

    // 登録する介護記録に紐づける利用者を取得する
    const resident: ResidentGetModel = await this.getResident(residentId);
    this.residentName = resident.name;
    this.residentRoom = resident.room;

    this.isBusy = false;

    return;
  }

  /**
   * 利用者取得
   */
  public async getResident(residentId: number): Promise<ResidentGetModel> {
    // 利用者IDは前画面から渡されているIDを使用する
    const response: ResidentGetModel = await this.residentsService.get(userStore, residentId);

    // ID指定のため、必ず利用者があるべき。ない場合は例外を投げる
    if (!response) {
      let error = new SystemError("[views/CareimageRegister.ts] getResident() : 利用者が存在しません。");
      throw error;
    }

    return response;
  }

  /**
   * ### 介護記録の追加イベント処理
   *
   * - ファイルが上限数以上なら無視する
   * - バリデーションを行う
   * - バリデーションエラーでなければ、ファイルを親コンポーネントに渡す
   */
  public async inputFiles(files: Array<File>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const careimageCommonService: CareimageCommonService = new CareimageCommonService(this.$validator, "careimage-multiple-register-errors");
    const addFiles: Array<CareimageFileModel> = await careimageCommonService.createAddFile(files, this.residentName, this.registerFileUploadLimit, this.imageFiles.length);

    this.isBusy = false;

    if (0 < addFiles.length) {
      this.imageFiles = this.imageFiles.concat(addFiles);

      for (let i = 0; i < this.imageFiles.length; i++) {
        this.imageFiles[i].key = i.toString();
      }
    }

    return;
  }

  /**
   * ### Base64文字列からFileインスタンスに変換する
   *
   * @param base64File Base64文字列
   */
  public convertBase64ToFile(base64File: string, name: string): File {
    // Base64文字列からコンテンツタイプを取得する
    const contentType = base64File.toString().slice(base64File.indexOf(":") + 1, base64File.indexOf(";"));

    // Base64文字列をバッファに変換して、バッファからFileインスタンスを作る
    const binary: string = atob(base64File.replace(/^.*,/, ""));
    const buffer: Uint8Array = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) {
      buffer[i] = binary.charCodeAt(i);
    }
    const file: File = new File([buffer.buffer], name, { type: contentType });

    return file;
  }

  /**
   * ### プレビューボタン押下イベント処理
   *
   * - ダイアログで介護記録をプレビュー表示する
   */
  public async onPreview(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const careimageModal: CareimageModal | Vue | Element | Vue[] | Element[] = this.$refs.careimageModal;
    if (careimageModal instanceof CareimageModal) {
      /**
       * CareimageModalコンポーネントは使いまわすコンポーネントのため、propsとして値を渡すと
       * CareimageModalの値更新タイミングよりモーダル表示が先に来てしまう可能性がある
       * そのため、モーダル表示メソッドの引数で表示する内容を渡す
       */
      const title: string = `${this.date}の${this.residentName}様`;
      const urls: Array<string> = [];
      for (const image of this.imageFiles) {
        urls.push(image.dataUrl);
      }

      careimageModal.show(title, false, urls);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ファイルをBase64エンコードする
   *
   * @param file ファイル
   */
  public async encode(file: File): Promise<string> {
    let fileUrl: string = "";
    // ファイルを画面で表示できるようにBase64エンコードする
    fileUrl = await FileReaderWrapper.readAsDataURL(file);

    return fileUrl;
  }

  /**
   * ### 指定されたファイルを削除する
   */
  public onFileDelete(index: number): void {
    this.imageFiles.splice(index, 1);

    return;
  }

  /**
   * ### 登録ボタン押下イベント
   */
  public async onRegisterCareimage(): Promise<void> {
    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.$validator.errors.remove("image-files");
    let validImageFiles: boolean = true;
    if (this.imageFiles.length < 1) {
      this.$validator.errors.add({ field: "image-files", msg: `介護記録は必須入力です` });
      validImageFiles = false;
    }
    for (const file of this.imageFiles) {
      if (!file || !file.name || file.size == 0) {
        this.$validator.errors.add({
          field: "image-files",
          msg: `ファイルが破損等をしている可能性があり、ファイルを認識することができません。お手数ですが、再度選択し直してください。`
        });
        validImageFiles = false;
      }
    }

    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult && validImageFiles) {
      this.showValidationAlert = false;
      await this.registerCareimage();
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * 介護記録登録
   */
  public async registerCareimage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    // ファイルアップロード先のURLを発行し、アップロードする
    let urls: Array<S3LocationResponse> = await this.createUploadUrls(this.imageFiles);
    for (let i = 0; i < urls.length; i++) {
      await this.s3UploadsService.uploadBase64File(urls[i], this.imageFiles[i].dataUrl);
    }

    const careimage: CareimageModel = this.createRegisterParam(urls);
    await this.careimagesService.post(userStore, Number(this.$route.params.id), careimage);

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async createUploadUrls(files: Array<CareimageFileModel>): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split(".");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");

      let extension: string = _extensions[_extensions.length - 1];
      if (extension.match(/jpg/)) {
        extension = "jpeg";
      }
      // 拡張子がpdfの場合は実際にはjpegに変換されているのでpdfに直す
      else if (extension.match(/pdf/)) {
        extension = "jpeg";
      }

      extensions.push("." + extension);
    }
    const params: S3LocationCareimage = {
      residentId: Number(this.$route.params.id),
      datetime: Number(this.$route.params.date)
    };
    const requestBody: S3LocationRequest = {
      type: S3LocationType.CAREIMAGE,
      count: files.length,
      extensions: extensions,
      params: params
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * 介護記録登録パラメータ作成
   */
  public createRegisterParam(urls: Array<S3LocationResponse>): CareimageModel {
    let imageUrls: Array<string> = [];
    for (const url of urls) {
      const preUrl: string = url.url.endsWith("/") ? url.url : `${url.url}/`;
      const imageUrl: string = `${preUrl}${url.fields["key"]}`;
      imageUrls.push(imageUrl);
    }

    const careimage: CareimageModel = {
      // Urlから取得した情報は一律string型になっているのでNumberに直す
      datetime: Number(this.$route.params.date),
      isPublic: false,
      deleteFlag: false,
      facilityId: userStore.loginUser.facilityId,
      imageUrls: imageUrls,
      residentId: Number(this.$route.params.id),
      residentName: this.residentName,
      residentRoom: this.residentRoom,
      writtenBy: userStore.loginUser.name,
      writerId: userStore.loginUser.userId
    };

    return careimage;
  }

  /**
   * 介護記録一覧に遷移する
   */
  public gotoCareimageList(): void {
    this.$router.push(PcUrl.CareimageList);

    return;
  }

  /**
   * ### 登録可能な介護記録画像数を返す
   */
  get _registerImageFilesLimit(): number {
    const limit: number = this.registerFileUploadLimit - this.imageFiles.length;
    return limit;
  }

  get _enablePreviewStatus(): boolean {
    return 0 < this.imageFiles.length ? true : false;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#careimage-register {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .box-body {
    // 介護記録とプレビューを並べて表示するため、横幅を指定
    .careimage-group {
      width: 400px;
    }

    .right-side {
      margin-left: auto;
    }
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
